import { Box, Stack } from "@mui/material";
import {
  MainAppLayoutContent,
  MainAppLayoutDrawer,
  MainAppLayoutHeader,
  MainAppLayoutHeaderDrawerButton,
  MainAppLayoutNavbar,
  MainAppLayoutProfileSection,
  MainAppLayoutRoot,
  MainAppLayoutWrapper,
  MobileOnly,
} from "@osn/react-components";
import React from "react";
import AppTitle from "./AppTitle";
import type { PageParent } from "./page-parent";
import PageTitleProvider from "./PageTitleProvider";
import ParticipantHeaderTitle from "./ParticipantHeaderTitle";

type UnloggedUserLayoutProps = {
  children: React.ReactNode;
  /** Same contract as `LoggedUserLayout`'s `title`. */
  title?: string;
  /** Same contract as `LoggedUserLayout`'s `parents`. */
  parents?: PageParent[];
  rightButton?: React.ReactNode;
  leftButton?: React.ReactNode;
  /**
   * Auth pages (sign in) render no drawer button: there the hamburger only opened a drawer listing the page itself.
   * The brand then takes the header's leading inset instead of sitting against the edge.
   */
  hideDrawerButton?: boolean;
  hideNav?: boolean;
  disablePadding?: boolean;
  laptopOffset?: number;
};

export function UnloggedUserLayout({
  children,
  title,
  parents,
  rightButton,
  leftButton,
  hideDrawerButton = false,
  hideNav = false,
  disablePadding = false,
  laptopOffset,
}: UnloggedUserLayoutProps) {
  const headerLeftButton = leftButton ?? <MainAppLayoutHeaderDrawerButton />;

  function selectUnloggedUserLayoutValue() {
    if (disablePadding) return undefined;
    return { mobile: 1, laptop: 2 };
  }
  function selectUnloggedUserLayoutValue2() {
    if (disablePadding) return 0;
    return 1;
  }
  function selectUnloggedUserLayoutValue3() {
    if (laptopOffset) return laptopOffset / 8 + 1;
    return 0;
  }
  function selectUnloggedUserLayoutValue4() {
    if (disablePadding) return undefined;
    return { laptop: 3 };
  }

  return (
    <MainAppLayoutRoot leftButton={leftButton} rightButton={rightButton}>
      <MainAppLayoutHeader>
        {!hideDrawerButton && headerLeftButton}
        {!hideDrawerButton && <AppTitle />}
        {hideDrawerButton && (
          <Box sx={{ minWidth: 0, pl: { mobile: 1.5, laptop: 2 } }}>
            <AppTitle />
          </Box>
        )}
        <ParticipantHeaderTitle parents={parents} />
        <MainAppLayoutProfileSection>{rightButton}</MainAppLayoutProfileSection>
      </MainAppLayoutHeader>
      <MainAppLayoutWrapper>
        <MainAppLayoutDrawer isAuthenticated={false} />
        <MainAppLayoutContent>
          <Stack
            sx={{
              flexGrow: 1,
              minHeight: 0,
              pt: selectUnloggedUserLayoutValue(),
              pb: { mobile: selectUnloggedUserLayoutValue2(), laptop: selectUnloggedUserLayoutValue3() },
              px: selectUnloggedUserLayoutValue4(),
            }}
          >
            <PageTitleProvider title={title} parents={parents}>
              {children}
            </PageTitleProvider>
          </Stack>
          {!hideNav && (
            <MobileOnly>
              <Box component="main" sx={{ minHeight: "56px", width: 1 }} />
              <MainAppLayoutNavbar />
            </MobileOnly>
          )}
        </MainAppLayoutContent>
      </MainAppLayoutWrapper>
    </MainAppLayoutRoot>
  );
}
